import type { Property } from '@/types/property'

/** Verified Unsplash URLs used across the site — safe to repeat on mock listings */
const PROPERTY_IMAGES = [
  'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&h=500&fit=crop&q=80',
  'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&h=500&fit=crop&q=80',
] as const

const img = (index: number) => PROPERTY_IMAGES[index % PROPERTY_IMAGES.length]

/** Build a gallery set for side-thumbnail scroll testing */
const galleryImages = (start: number, count = 8) =>
  Array.from({ length: count }, (_, i) => img(start + i))

const SEED_PROPERTIES: Property[] = [
  {
    id: 1001,
    slug: 'spacious-3bhk-satellite-ahmedabad',
    name: 'Spacious 3BHK Apartment with City View',
    image: img(0),
    images: galleryImages(0, 8),
    photoCount: 8,
    price: 8500000,
    city: 'Ahmedabad',
    area: 'Satellite',
    location: 'Satellite, Ahmedabad',
    bhk: '3 BHK',
    sqft: 1450,
    builder: 'Shanti Developers',
    postedAt: '2026-03-08T10:00:00Z',
    isVerified: true,
    isHot: true,
    isPremium: true,
    intent: 'buy',
    category: 'Apartment',
  },
  {
    id: 1002,
    slug: 'luxury-villa-bopal-ahmedabad',
    name: 'Luxury 4BHK Villa with Private Garden',
    image: img(1),
    images: galleryImages(1, 9),
    photoCount: 9,
    price: 24500000,
    city: 'Ahmedabad',
    area: 'Bopal',
    location: 'Bopal, Ahmedabad',
    bhk: '4 BHK',
    sqft: 3200,
    builder: 'Adani Realty',
    postedAt: '2026-03-05T10:00:00Z',
    isVerified: true,
    isHot: false,
    isPremium: true,
    intent: 'buy',
    category: 'Villa',
  },
  {
    id: 1003,
    slug: '2bhk-rent-sg-highway',
    name: 'Fully Furnished 2BHK for Rent',
    image: img(2),
    images: galleryImages(2, 7),
    photoCount: 7,
    price: 28000,
    city: 'Ahmedabad',
    area: 'SG Highway',
    location: 'SG Highway, Ahmedabad',
    bhk: '2 BHK',
    sqft: 1100,
    builder: 'Premium Living',
    postedAt: '2026-03-10T10:00:00Z',
    isVerified: true,
    isHot: true,
    isPremium: false,
    intent: 'rent',
    category: 'Apartment',
  },
  {
    id: 1004,
    slug: 'commercial-office-prahladnagar',
    name: 'Premium Office Space — Prahlad Nagar',
    image: img(0),
    images: galleryImages(3, 8),
    photoCount: 8,
    price: 12000000,
    city: 'Ahmedabad',
    area: 'Prahlad Nagar',
    location: 'Prahlad Nagar, Ahmedabad',
    bhk: 'Office',
    sqft: 2400,
    builder: 'QBC Commercial',
    postedAt: '2026-03-01T10:00:00Z',
    isVerified: false,
    isHot: false,
    isPremium: true,
    intent: 'commercial',
    category: 'Commercial',
  },
  {
    id: 1005,
    slug: '3bhk-gota-new-project',
    name: '3BHK in Gota — New Launch',
    image: img(1),
    images: galleryImages(4, 10),
    photoCount: 10,
    price: 6200000,
    city: 'Ahmedabad',
    area: 'Gota',
    location: 'Gota, Ahmedabad',
    bhk: '3 BHK',
    sqft: 1280,
    builder: 'Sunrise Group',
    postedAt: '2026-03-11T10:00:00Z',
    isVerified: true,
    isHot: true,
    isPremium: false,
    intent: 'buy',
    category: 'Apartment',
  },
  {
    id: 1006,
    slug: 'penthouse-vastrapur',
    name: 'Ultra Luxury Penthouse — Vastrapur',
    image: img(2),
    images: galleryImages(5, 12),
    photoCount: 12,
    price: 45000000,
    city: 'Ahmedabad',
    area: 'Vastrapur',
    location: 'Vastrapur, Ahmedabad',
    bhk: '5 BHK',
    sqft: 5200,
    builder: 'Elite Homes',
    postedAt: '2026-02-20T10:00:00Z',
    isVerified: true,
    isHot: false,
    isPremium: true,
    intent: 'buy',
    category: 'Penthouse',
  },
]

const BASE_LISTINGS = [...SEED_PROPERTIES]

const AREAS = ['Shela', 'Satellite', 'Bopal', 'Gota', 'SG Highway', 'Vastrapur', 'Prahlad Nagar', 'Maninagar']
const POSSESSION = ['Ready to move', 'Dec, 2026', 'Jul, 2030', 'Immediate']

function expandListings(): Property[] {
  const expanded: Property[] = []
  for (let i = 0; i < 36; i++) {
    const src = BASE_LISTINGS[i % BASE_LISTINGS.length]
    const area = AREAS[i % AREAS.length]
    const priceOffset = (i % 5) * 125000
    const basePrice = typeof src.price === 'number' ? src.price + priceOffset : src.price
    const priceMax =
      typeof basePrice === 'number' ? basePrice + 200000 + (i % 3) * 150000 : undefined
    const imageCount = 7 + (i % 5)
    const images = galleryImages(i, imageCount)
    expanded.push({
      ...src,
      id: 1001 + i,
      slug: `${src.slug}-listing-${i + 1}`,
      name: i < BASE_LISTINGS.length ? src.name : `${src.name.split('—')[0].trim()} — ${area}`,
      area,
      location: `${area}, ${src.city}`,
      image: images[0],
      images,
      photoCount: imageCount,
      price: basePrice,
      priceMax,
      agent: src.builder ? `${src.builder}` : undefined,
      possession: POSSESSION[i % POSSESSION.length],
      saleType: i % 3 === 0 ? 'New Bookings' : 'Resale',
      constructionStatus: i % 4 === 0 ? 'Under Construction' : 'Ready to move',
    })
  }
  return expanded
}

export const mockProperties: Property[] = expandListings()

export const featuredProperties = mockProperties.filter((p) => p.isVerified).slice(0, 8)
export const premiumProjects = mockProperties.filter((p) => p.isPremium).slice(0, 8)
